/* eslint-disable react-hooks/rules-of-hooks */
import {
  ProgramPartialUpdateData,
  ProgramPartialUpdateField,
  ProgramRow,
  ResolvedProgramEndDateField,
  ResolvedProgramStartDateField,
} from '../../types';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { CellProps } from './ProgramCells';
import DatePicker from 'react-datepicker';
import {
  displayEndDateToInternal,
  formatDateTimeCell,
  internalEndDateToDisplay,
} from '../../../util/date-utils';
import { GrowegySelectMockup } from '../GrowegySelectMockup';
import { recalculateDueDates } from '../tasks/smartDueDateUtils';
import { useIsSmallerThanTablet } from '../../../responsive/hooks/hooks';
import { isSocialPostingProgram, makeProgramSchedule } from '../../util';
import { EmptyCell } from './EmptyCell';

const DateTimeCell =
  (dataTest: string, field: ProgramPartialUpdateField) =>
  ({
    onProgramUpdate,
    onRowEditChange,
    value: origValue,
    row,
    readOnly,
  }: CellProps<ProgramRow, Date>) => {
    const [value, setValue] = useState<Date | null>(origValue);
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<DatePicker>(null);

    const isSmallerThanTablet = useIsSmallerThanTablet();

    useEffect(() => {
      setValue(origValue);
    }, [origValue]);

    useEffect(() => {
      if (!inputRef.current) return;
      inputRef.current?.setFocus();
    }, [editing]);

    const handleEditClick = (e: MouseEvent<HTMLDivElement>) => {
      if (row.isGrouped) return;
      e.stopPropagation();
      if (readOnly) return;
      if (editing) return;
      setEditing(true);
      onRowEditChange(true);
    };

    const confirm = (value: Date | null) => {
      if (field.type === 'ArrayFieldItem' && field.arrayField === 'customFields') {
        onProgramUpdate({
          original: row.original,
          updates: [
            {
              field: 'customFields',
              customFieldName: field.arrayItemValue.name,
              customFieldData: {
                type: 'date-time',
                value: value,
              },
            },
          ],
        });
      } else if (
        field.type === 'StaticField' &&
        (field.fieldName === ResolvedProgramStartDateField ||
          field.fieldName === ResolvedProgramEndDateField)
      ) {
        if (!value) return;

        const originalStart = row.original.startDateTime;
        const originalEnd = row.original.endDateTime;
        const wholeDay = row.original.wholeDay;

        const programSchedule = makeProgramSchedule({
          type: 'SCHEDULED',
          rangeStart: field.fieldName === ResolvedProgramStartDateField ? value : originalStart,
          rangeEnd:
            field.fieldName === ResolvedProgramEndDateField
              ? displayEndDateToInternal(value, wholeDay)
              : originalEnd,
          wholeDay,
          isSocialPosting: isSocialPostingProgram(row.original),
        });

        const updates: ProgramPartialUpdateData[] = [];
        if (programSchedule.startDateTime.valueOf() !== originalStart.valueOf()) {
          updates.push({ field: 'startDateTime', value: programSchedule.startDateTime });
        }
        if (programSchedule.endDateTime.valueOf() !== originalEnd.valueOf()) {
          updates.push({ field: 'endDateTime', value: programSchedule.endDateTime });
        }
        const updatedTasks = recalculateDueDates(programSchedule, row.original.resolvedTasks, true);
        const tasksAfterChange = recalculateDueDates(
          programSchedule,
          row.original.resolvedTasks,
          false
        );
        if (updatedTasks && tasksAfterChange && updatedTasks.length > 0) {
          updates.push({
            field: 'resolvedTasks',
            value: {
              changes: updatedTasks.map((x) => ({ changeType: 'UPDATE', task: x })),
              tasksAfterChange,
            },
          });
        }

        if (updates.length > 0) {
          onProgramUpdate({ original: row.original, updates });
        }
      }
      setEditing(false);
      onRowEditChange(false);
    };

    const cancel = () => {
      setValue(origValue);
      setEditing(false);
      onRowEditChange(false);
    };

    const displayValue =
      value && field.type === 'StaticField' && field.fieldName === ResolvedProgramEndDateField
        ? internalEndDateToDisplay(value, row.original.wholeDay)
        : value;

    const showTime = field.type !== 'StaticField' || !row.original?.wholeDay;

    if (
      field.type === 'StaticField' &&
      field.fieldName === ResolvedProgramEndDateField &&
      isSocialPostingProgram(row.original)
    )
      return <EmptyCell />;

    return (
      <div
        data-test={dataTest}
        onClick={handleEditClick}
        onKeyDown={(e) => {
          if (editing) e.stopPropagation();
        }}
      >
        {editing ? (
          <DatePicker
            showTimeSelect={showTime}
            ref={inputRef}
            onBlur={cancel}
            onClickOutside={cancel}
            onCalendarClose={() => confirm(value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                confirm(value);
              }
              if (e.key === 'Escape') {
                cancel();
              }
            }}
            timeIntervals={15}
            dateFormat={showTime ? 'MM/dd/yyyy, hh:mm a' : 'MM/dd/yyyy'}
            selected={displayValue}
            minDate={
              field.type === 'StaticField' && field.fieldName === ResolvedProgramEndDateField
                ? row.original.startDateTime
                : undefined
            }
            onSelect={(d) => confirm(d)}
            onChange={(d: Date | null) => setValue(d)}
            className="programs-table__cell-input"
            readOnly={readOnly}
            isClearable={false}
            withPortal={isSmallerThanTablet}
          />
        ) : (
          <GrowegySelectMockup readOnly={readOnly}>
            {formatDateTimeCell(displayValue, showTime ? 'MMM DD, hh:mm a' : 'MMM DD')}
          </GrowegySelectMockup>
        )}
      </div>
    );
  };

export default DateTimeCell;
